/**
 * Plan konta wyliczony z profilu `GET /api/v1/account/me` (FE-01). Kontrakt pól: repo VV,
 * `zadania/2026-10_plany-free-premium.md`, sekcja „Kontrakt”.
 *
 * ═══ STANY ═══
 * `ustalanie`   sesja albo profil jeszcze się wczytują: nie pokazujemy ani funkcji, ani kłódki.
 * `bez_konta`   niezalogowany.
 * `bez_planow`  zalogowany, ale profil nie ma pola `funkcje` (backend sprzed BE-02) albo profil nie doszedł.
 *               Działa jak dziś: bez blokad planu. Dzięki temu front można wdrożyć przed backendem.
 * `plan`        zalogowany z planem: `funkcje` to lista funkcji Premium dostępnych teraz (pusta we Free).
 *
 * Blokada w interfejsie to zasłona, nie kontrola dostępu: tę robi backend (402). Pytamy o funkcję, nie o plan.
 */
import { FUNKCJE_PREMIUM, LIMIT_KONT } from '../config/plany'

export const wyliczPlan = ({ zalogowany, ustalanie, profil }) => {
  if (ustalanie) return { stan: 'ustalanie' }
  if (!zalogowany) return { stan: 'bez_konta' }
  if (!profil || !Array.isArray(profil.funkcje)) return { stan: 'bez_planow' }
  const firma = profil.company || null
  // Konto bez firmy ma własny plan w `plan_konta` (backend `PLANY_PROBA_BEZ_FIRMY`, 2026-10-19), w tym samym kształcie.
  const zrodlo = firma || profil.plan_konta || null
  return {
    stan: 'plan',
    firmowe: Boolean(firma),
    plan: zrodlo?.plan === 'premium' ? 'premium' : 'free',
    proba: Boolean(zrodlo?.proba),
    probaDo: zrodlo?.proba_do || null,
    dniProby: Number.isFinite(zrodlo?.dni_proby) ? zrodlo.dni_proby : null,
    premiumDo: zrodlo?.premium_do || null,
    limitKont: Number.isFinite(firma?.limit_kont) ? firma.limit_kont : null,
    zablokowane: Boolean(firma?.zablokowane),
    funkcje: profil.funkcje,
  }
}

/**
 * Czy wolno użyć funkcji Premium. `powod` mówi, co pokazać zamiast niej: `bez_konta` (zachęta do rejestracji)
 * albo `premium` (okno „Przejdź na Premium”).
 */
export const dostepFunkcji = (plan, funkcja) => {
  if (plan.stan === 'ustalanie') return { mozna: false, ustalanie: true, powod: null }
  if (plan.stan === 'bez_konta') return { mozna: false, ustalanie: false, powod: 'bez_konta' }
  if (plan.stan !== 'plan' || plan.funkcje.includes(funkcja)) return { mozna: true, ustalanie: false, powod: null }
  return { mozna: false, ustalanie: false, powod: 'premium' }
}

// ═══ PODGLĄD PLANÓW NA LOCALHOST ═══
// `?plan=free` (i inne warianty niżej) nadpisuje pola planu w profilu, żeby oglądać wszystkie stany, zanim backend
// je poda. Działa TYLKO na adresie lokalnym (localhost, 127.0.0.1, *.localhost), także w buildzie z Dockera; na
// voltvalue.pl nic nie robi. Wariant żyje w `sessionStorage` tej karty, `?plan=wyczysc` go zdejmuje.

const KLUCZ_PODGLADU = 'vv:podglad-planu'

const dzisPlus = (dni) => {
  const d = new Date()
  d.setDate(d.getDate() + dni)
  return d.toISOString().slice(0, 10)
}

const wszystkieFunkcje = () => Object.keys(FUNKCJE_PREMIUM)

export const WARIANTY_PODGLADU = {
  'bez-planu': { etykieta: 'backend bez planów (jak dziś)' },
  free: { etykieta: 'Free' },
  proba: { etykieta: 'Premium na próbę, 12 dni' },
  'proba-koniec': { etykieta: 'Premium na próbę, ostatni dzień' },
  premium: { etykieta: 'Premium' },
  prywatne: { etykieta: 'konto prywatne' },
  zablokowane: { etykieta: 'Free, konto ponad limitem' },
}

export const adresLokalny = (host) => host === 'localhost' || host === '127.0.0.1' || host === '[::1]'
  || host.endsWith('.localhost')

/** Wariant podglądu z adresu albo z pamięci karty; `null` poza adresem lokalnym. */
export const odczytajPodglad = (loc = window.location) => {
  if (!adresLokalny(loc.hostname)) return null
  try {
    const par = new URLSearchParams(loc.search).get('plan')
    if (par === 'wyczysc') window.sessionStorage.removeItem(KLUCZ_PODGLADU)
    else if (par && WARIANTY_PODGLADU[par]) window.sessionStorage.setItem(KLUCZ_PODGLADU, par)
    const zapisany = window.sessionStorage.getItem(KLUCZ_PODGLADU)
    return WARIANTY_PODGLADU[zapisany] ? zapisany : null
  } catch {
    return null
  }
}

/** Profil z polami planu podmienionymi na wariant podglądu. Bez profilu (niezalogowany) nic nie zmienia. */
export const profilZPodgladem = (profil, wariant) => {
  if (!profil || !wariant) return profil
  const firma = profil.company || { id: 0, name: 'Firma podglądu', role: 'owner', uprawnienia: [] }
  const zFirma = (pola, funkcje) => ({
    ...profil, account_type: 'company', funkcje, company: { ...firma, ...pola },
  })
  switch (wariant) {
    case 'bez-planu': {
      const bezFunkcji = { ...profil }
      delete bezFunkcji.funkcje
      return bezFunkcji
    }
    case 'free':
      return zFirma({ plan: 'free', proba: false, proba_do: null, dni_proby: null, premium_do: null,
        limit_kont: LIMIT_KONT.free, zablokowane: false }, [])
    case 'proba':
      return zFirma({ plan: 'premium', proba: true, proba_do: dzisPlus(11), dni_proby: 12, premium_do: null,
        limit_kont: LIMIT_KONT.premium, zablokowane: false }, wszystkieFunkcje())
    case 'proba-koniec':
      return zFirma({ plan: 'premium', proba: true, proba_do: dzisPlus(0), dni_proby: 1, premium_do: null,
        limit_kont: LIMIT_KONT.premium, zablokowane: false }, wszystkieFunkcje())
    case 'premium':
      return zFirma({ plan: 'premium', proba: false, proba_do: null, dni_proby: null, premium_do: dzisPlus(365),
        limit_kont: LIMIT_KONT.premium, zablokowane: false }, wszystkieFunkcje())
    case 'prywatne':
      return { ...profil, account_type: 'private', company: null, funkcje: [] }
    case 'zablokowane':
      return zFirma({ plan: 'free', proba: false, proba_do: null, dni_proby: null, premium_do: null,
        limit_kont: LIMIT_KONT.free, zablokowane: true, role: 'member' }, [])
    default:
      return profil
  }
}
